import React from "react";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  CommunicationFeedbackActionType,
  CommunicationFeedbackFollowUp,
} from "@/types/feedback-settings";
import { useTranslation } from "react-i18next";

interface CommunicationFollowUpCardProps {
  followUp: CommunicationFeedbackFollowUp;
  canRemove: boolean;
  onChange: (followUp: CommunicationFeedbackFollowUp) => void;
  onRemove: () => void;
  onAddAfter: () => void;
}

const actionTypes: CommunicationFeedbackActionType[] = [
  "SEND_MESSAGE",
  "OPEN_CHAT_PROMPT",
];
const ratingOptions = [1, 2, 3, 4, 5];

const stopSortableKeyboardEvent = (
  event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
) => {
  event.stopPropagation();
};

export const CommunicationFollowUpCard = ({
  followUp,
  canRemove,
  onChange,
  onRemove,
  onAddAfter,
}: CommunicationFollowUpCardProps): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className="border-border bg-background rounded-lg border p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-muted-foreground flex items-center gap-2 text-sm font-semibold">
          <GripVertical className="size-4 cursor-grab active:cursor-grabbing" />
          {t("feedbacks.settings.communication.responseTitle", {
            order: followUp.order,
          })}
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-muted-foreground flex items-center gap-2 text-sm">
            <Switch
              checked={followUp.isActive}
              onCheckedChange={(isActive) =>
                onChange({
                  ...followUp,
                  isActive,
                })
              }
            />
            {t("feedbacks.settings.communication.active")}
          </Label>

          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={onAddAfter}
          >
            <Plus className="size-4" />
            <span className="sr-only">
              {t("feedbacks.settings.communication.addResponse")}
            </span>
          </Button>

          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={!canRemove}
            onClick={onRemove}
          >
            <Trash2 className="size-4" />
            <span className="sr-only">
              {t("feedbacks.settings.communication.removeResponse")}
            </span>
          </Button>
        </div>
      </div>

      <div className="mt-4 grid gap-4">
        <Input
          value={followUp.title}
          onKeyDown={stopSortableKeyboardEvent}
          onChange={(event) =>
            onChange({
              ...followUp,
              title: event.target.value,
            })
          }
          placeholder={t("feedbacks.settings.communication.titlePlaceholder")}
        />

        <div className="grid gap-4 md:grid-cols-2">
          <Label className="flex flex-col gap-2 text-sm font-medium">
            {t("feedbacks.settings.communication.actionType")}
            <Select
              value={followUp.actionType}
              onValueChange={(value) =>
                onChange({
                  ...followUp,
                  actionType: value as CommunicationFeedbackActionType,
                })
              }
            >
              <SelectTrigger className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {actionTypes.map((actionType) => (
                  <SelectItem key={actionType} value={actionType}>
                    {t(
                      `feedbacks.settings.communication.actionTypes.${actionType}`,
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Label>

          <div className="grid grid-cols-2 gap-4">
            <Label className="flex flex-col gap-2 text-sm font-medium">
              {t("feedbacks.settings.communication.minRating")}
              <Select
                value={String(followUp.minRating)}
                onValueChange={(value) =>
                  onChange({
                    ...followUp,
                    minRating: Number(value),
                    maxRating: Math.max(Number(value), followUp.maxRating),
                  })
                }
              >
                <SelectTrigger className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ratingOptions.map((rating) => (
                    <SelectItem key={rating} value={String(rating)}>
                      {t("feedbacks.settings.communication.ratingValue", {
                        count: rating,
                      })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Label>

            <Label className="flex flex-col gap-2 text-sm font-medium">
              {t("feedbacks.settings.communication.maxRating")}
              <Select
                value={String(followUp.maxRating)}
                onValueChange={(value) =>
                  onChange({
                    ...followUp,
                    minRating: Math.min(followUp.minRating, Number(value)),
                    maxRating: Number(value),
                  })
                }
              >
                <SelectTrigger className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ratingOptions.map((rating) => (
                    <SelectItem key={rating} value={String(rating)}>
                      {t("feedbacks.settings.communication.ratingValue", {
                        count: rating,
                      })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Label>
          </div>
        </div>

        <Textarea
          value={followUp.messageTemplate}
          onKeyDown={stopSortableKeyboardEvent}
          onChange={(event) =>
            onChange({
              ...followUp,
              messageTemplate: event.target.value,
            })
          }
          placeholder={t(
            "feedbacks.settings.communication.messageTemplatePlaceholder",
          )}
        />
      </div>
    </div>
  );
};
